import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReportProblemButton } from "@/components/ReportProblem";
import api, { API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Navbar } from "@/components/Navbar";
import { Play, Download, Library as LibraryIcon, Loader2, ArrowLeft, Layers, FileText, Package, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Library() {
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [resending, setResending] = useState(null);

  const resendLinks = async (track) => {
    setResending(track.id);
    try {
      const { data } = await api.post(`/purchases/${track.id}/resend-links`);
      toast.success(`Fresh download links sent to ${data.email} — they work for ${data.expires_hours} hours.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the email. Please try again.");
    } finally {
      setResending(null);
    }
  };

  useEffect(() => {
    if (user === false) { navigate("/"); return; }
    if (user) {
      api.get("/purchases").then((r) => setTracks(r.data)).finally(() => setLoading(false));
    }
  }, [user, navigate]);

  const download = async (track, kind = "audio") => {
    const key = kind === "audio" ? track.id : `${track.id}-${kind}`;
    setDownloading(key);
    try {
      // Prefer a signed cloud-storage URL (works for multi-GB files, no proxying).
      const { data: link } = await api.get(`/download-link/${track.id}?kind=${kind}`);
      if (link && link.url) {
        const a = document.createElement("a");
        a.href = link.url;
        a.download = link.filename || track.title;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      // Legacy fallback: stream the file through the backend (audio/stems only).
      if (!["audio", "stems"].includes(kind)) throw new Error("no signed link");
      const path = kind === "stems" ? `/download-stems/${track.id}` : `/download/${track.id}`;
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "stems" ? `${track.title}-stems.${track.stems_format || "zip"}` : `${track.title}.${(track.audio_format || "mp3").toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen grain pb-44">
      <Navbar />
      <div className="px-6 md:px-12 py-10 relative z-10">
        <button onClick={() => navigate("/")} data-testid="back-home-btn"
          className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>

        <div className="flex items-center gap-3 mb-8">
          <LibraryIcon className="w-7 h-7 text-[#e2ff31]" />
          <h1 data-testid="purchases-title" className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">My Purchases</h1>
        </div>
        <p className="text-sm text-[#6b7280] -mt-5 mb-8">Download links are private, expire after 30 minutes, and only work for the account that bought the beat.</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e2ff31]" /></div>
        ) : tracks.length === 0 ? (
          <div data-testid="empty-library" className="text-center py-24 border border-dashed border-[#2a2a2a] rounded-2xl">
            <LibraryIcon className="w-12 h-12 text-[#2a2a2a] mx-auto mb-4" />
            <p className="text-[#9ca3af] mb-4">You haven't purchased any beats yet.</p>
            <button onClick={() => navigate("/")} className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-6 py-2.5 transition-colors">
              Browse beats
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tracks.map((t) => (
              <div key={t.id} data-testid={`library-track-${t.id}`}
                className="bg-[#121212] border border-[#2a2a2a] rounded-xl overflow-hidden group">
                <div className="relative aspect-square bg-[#1e1e1e]">
                  <img src={`${API}${t.cover_url}`} alt={t.title} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-full h-full object-cover" />
                  <button onClick={() => playTrack(t, tracks)} data-testid={`library-play-${t.id}`}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-14 h-14 rounded-full bg-[#e2ff31] text-black flex items-center justify-center">
                      <Play className="w-6 h-6 ml-1" fill="currentColor" />
                    </span>
                  </button>
                </div>
                <div className="p-4">
                  <p className="font-medium truncate">{t.title}</p>
                  <p className="text-sm text-[#9ca3af] truncate">{t.producer}</p>
                  <p data-testid={`purchased-at-${t.id}`} className="text-xs font-mono text-[#6b7280] mb-3">
                    {t.purchased_at ? `Purchased ${new Date(t.purchased_at).toLocaleDateString()}` : "Purchased"} · Exclusive
                  </p>
                  <button onClick={() => download(t)} disabled={downloading === t.id} data-testid={`download-track-${t.id}`}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold py-2.5">
                    {downloading === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download {t.audio_format || "MP3"}
                  </button>
                  {t.wav_available && t.audio_format !== "WAV" && (
                    <button onClick={() => download(t, "wav")} disabled={downloading === t.id + "-wav"} data-testid={`download-wav-${t.id}`}
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-full border border-[#e2ff31]/50 text-[#e2ff31] hover:bg-[#e2ff31]/10 active:scale-95 transition-all font-semibold py-2.5">
                      {downloading === t.id + "-wav" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Download WAV (master)
                    </button>
                  )}
                  {t.stems_included && (
                    <button onClick={() => download(t, "stems")} disabled={downloading === t.id + "-stems"} data-testid={`download-stems-${t.id}`}
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-full border border-[#e2ff31]/50 text-[#e2ff31] hover:bg-[#e2ff31]/10 active:scale-95 transition-all font-semibold py-2.5">
                      {downloading === t.id + "-stems" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                      Download Stems ({(t.stems_format || "").toUpperCase()})
                    </button>
                  )}
                  {t.bundle_available && (
                    <button onClick={() => download(t, "bundle")} disabled={downloading === t.id + "-bundle"} data-testid={`download-bundle-${t.id}`}
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-full border border-[#2a2a2a] text-[#f3f4f6] hover:bg-[#1e1e1e] active:scale-95 transition-all font-semibold py-2.5">
                      {downloading === t.id + "-bundle" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                      Download Bundle (ZIP)
                    </button>
                  )}
                  {t.license_available && (
                    <button onClick={() => download(t, "license")} disabled={downloading === t.id + "-license"} data-testid={`download-license-${t.id}`}
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-full text-[#9ca3af] hover:text-white active:scale-95 transition-all text-sm py-2">
                      {downloading === t.id + "-license" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                      License Agreement (PDF)
                    </button>
                  )}
                  <button onClick={() => resendLinks(t)} disabled={resending === t.id} data-testid={`resend-links-${t.id}`}
                    className="mt-1 w-full flex items-center justify-center gap-2 rounded-full text-xs text-[#6b7280] hover:text-[#e2ff31] transition-colors py-2">
                    {resending === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Resend my download link
                  </button>
                  <ReportProblemButton track={t} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
